import React from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  isRuntimeVarInvalid,
  runtimeVarKeyError,
  type RuntimeVar,
  type RuntimeVarType,
} from './useStudioDraft';
import classes from './studio.module.scss';

export interface RuntimeVariablesPanelProps {
  value: RuntimeVar[];
  onChange: (next: RuntimeVar[]) => void;
  'data-testid'?: string;
}

const TYPES: RuntimeVarType[] = ['String', 'Number', 'Secret'];

/**
 * Runtime variables (KEY / TYPE / VALUE) passed to `process()` via kwargs.
 * Serialized into the code container's extra_data by {@link useStudioDraft}.
 */
export function RuntimeVariablesPanel({
  value,
  onChange,
  'data-testid': dataTestId = 'byo-runtime-vars',
}: RuntimeVariablesPanelProps) {
  const update = (i: number, patch: Partial<RuntimeVar>) =>
    onChange(value.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { key: '', type: 'String', value: '' }]);

  return (
    <Box data-testid={dataTestId}>
      <Typography className={classes.sectionTitle}>
        Runtime variables &amp; configuration
      </Typography>
      <Typography className={classes.sectionSubtitle}>
        Passed to your <code>process()</code> via kwargs.
      </Typography>

      <Box className={classes.pkgTable}>
        <Box className={classes.pkgHeadRow}>
          <span>Key</span>
          <span>Type</span>
          <span>Value</span>
          <span />
        </Box>
        {value.map((row, i) => {
          // A Number-typed row with non-numeric text silently became JSON
          // `null` in the persisted extra_data (Number('abc') → NaN →
          // JSON.stringify(NaN) → null), with no validation anywhere — the
          // pipeline's Python code got `None` instead of the intended value,
          // with zero indication why. `type="number"` blocks most invalid
          // input at entry; this is the visible fallback for what it can't
          // (e.g. an emptied field). Confirmed bug, fixed 2026-08-20.
          const keyError = runtimeVarKeyError(row, value);
          const numberInvalid = !!row.key.trim() && isRuntimeVarInvalid(row);
          return (
            <Box className={classes.pkgRow} key={i}>
              <TextField
                size="small"
                placeholder="KEY"
                value={row.key}
                error={!!keyError}
                helperText={keyError ?? undefined}
                onChange={(e) => update(i, { key: e.target.value })}
                inputProps={{
                  'aria-label': 'Variable key',
                  'data-testid': `${dataTestId}-key-${i}`,
                }}
              />
              <TextField
                size="small"
                select
                value={row.type}
                onChange={(e) => update(i, { type: e.target.value as RuntimeVarType })}
                inputProps={{ 'aria-label': 'Variable type' }}
              >
                {TYPES.map((t) => (
                  <MenuItem value={t} key={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type={
                  row.type === 'Secret' ? 'password' : row.type === 'Number' ? 'number' : 'text'
                }
                placeholder="value"
                value={row.value}
                error={numberInvalid}
                helperText={numberInvalid ? 'Not a number' : undefined}
                onChange={(e) => update(i, { value: e.target.value })}
                inputProps={{
                  'aria-label': 'Variable value',
                  'data-testid': `${dataTestId}-value-${i}`,
                }}
              />
              <IconButton size="small" onClick={() => remove(i)} aria-label="Remove variable">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Button onClick={add} className={classes.pkgAdd} data-testid={`${dataTestId}-add`}>
          + Add variable
        </Button>
      </Box>
    </Box>
  );
}

export default RuntimeVariablesPanel;
